import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      navigate("/scan");
    } catch (e2) {
      setErr(apiError(e2));
    } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-md">
      <div className="mb-6 flex flex-col items-center text-center">
        <Logo size={64} />
        <h1 className="mt-4 text-2xl font-bold text-gray-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("auth.loginTitle")}</h1>
        <p className="mt-1 text-sm text-gray-500">{t("auth.loginSub")}</p>
      </div>

      <form onSubmit={submit} className="rounded-2xl border border-gray-200 bg-white p-6" data-testid="login-form">
        {err && <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600" data-testid="login-error">{err}</div>}
        <label className="mb-1.5 block text-sm font-semibold text-gray-700">{t("auth.email")}</label>
        <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          className="mb-4 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" placeholder="you@email.com" />
        <label className="mb-1.5 block text-sm font-semibold text-gray-700">{t("auth.password")}</label>
        <div className="relative mb-2">
          <input data-testid="login-password-input" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-gray-200 px-4 py-3 pr-11 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" placeholder="••••••••" />
          <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <div className="mb-5 text-right">
          <Link to="/forgot-password" className="text-sm font-semibold text-emerald-600 hover:underline">{t("auth.forgot")}</Link>
        </div>
        <button data-testid="login-submit-button" disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 py-3.5 text-sm font-bold text-white transition-colors hover:bg-emerald-600 disabled:opacity-60">
          {loading && <Loader2 size={16} className="animate-spin" />} {t("auth.login")}
        </button>
      </form>

      <p className="mt-5 text-center text-sm text-gray-500">
        {t("auth.noAccount")} <Link to="/register" className="font-semibold text-emerald-600 hover:underline">{t("auth.signup")}</Link>
      </p>
    </div>
  );
}
